export const customerPreview = {
    selectCustomer: 'Select customer',
    refreshCustomers: 'Refresh customers',
    loadingCustomers: 'Loading customers...',
    noCustomerSelected: 'Select a customer to preview usage and billing.',
    previewingCustomer: 'Previewing customer',
    safetyNotes: 'Safety notes',
    safetyNotesDescription:
      'Keep portal links and pay links limited-use. Avoid exposing Stripe IDs or metadata directly.',
    changePlanTitle: 'Change plan',
    changePlanDescription:
      'Provide a plan ID to switch this subscription. Proration is recorded but Stripe proration is pending backend support.',
    planIdLabel: 'Plan',
    planTypeFilterLabel: 'Plan type',
    planTypeFilterAll: 'All plans',
    planTypeFilterIndividual: 'Individual',
    planTypeFilterTeam: 'Team',
    planIdPlaceholder: 'plan_...',
    planSelectPlaceholder: 'Select a plan',
    planSelectLoading: 'Loading plans...',
    planSelectEmpty: 'No plans available',
    currentBadge: 'Current',
    variantBadge: 'Variant',
    planVariantSuffix: '(Variant)',
    planCycleLabel: 'Billing cycle',
    planCyclePlaceholder: 'Select a billing cycle',
    planCycleEmpty: 'No billing cycles available',
    planIdRequired: 'Plan is required',
    planAlreadyCurrent: 'The selected plan is already active.',
    planCycleRequired: 'Billing cycle is required',
    planCycleUnavailable: 'No billing cycles are available for the selected plan.',
    workspaceNameLabel: 'Workspace name',
    workspaceNamePlaceholder: 'Enter workspace name',
    workspaceNameHelper: 'Required when switching from individual to team plan.',
    workspaceNameRequired: 'Workspace name is required.',
    workspaceNameMinLength: 'Workspace name must be at least 2 characters.',
    workspaceNameMaxLength: 'Workspace name must be 255 characters or less.',
    subscriptionNotFoundTitle: 'Subscription not found',
    subscriptionNotFound:
      'Subscription was not found. Refresh customer context and try again.',
    subscriptionNotFoundToast: 'Subscription not found. Refresh and try again.',
    prorateChange: 'Prorate change',
    prorateHint: 'Uses backend proration metadata.',
    changePlanCta: 'Change plan',
    planChangeSuccess: 'Plan changed',
    planChangeSuccessWithCycle: 'Plan changed to {{plan}} ({{cycle}})',
    planChangeError: 'Unable to change plan',
    noSubscriptionSelectionUnavailable:
      'No active subscription exists yet. Refresh customer context before selecting a plan.',
    billingPlan: 'Billing plan',
    billingPlanDescription: 'Current subscription, price, entitlements, and actions.',
    planLabel: 'Plan',
    noPlan: 'No plan selected',
    noPrice: 'No price',
    introPriceLabel: 'Intro price',
    basePriceLabel: 'Base price',
    basePriceStarts: 'Base price starts',
    basePriceAfterIntro: 'After intro period',
    quantity: 'Quantity',
    period: 'Period',
    trialRemaining_one: 'Trial ends in {{count}} day',
    trialRemaining_other: 'Trial ends in {{count}} days',
    entitlements: 'Entitlements',
    entitlementSeats: 'Seats',
    entitlementProjects: 'Projects',
    entitlementStorage: 'Storage',
    entitlementSupport: 'Support',
    noEntitlements: 'No entitlements provided.',
    trialEnds: 'Trial ends',
    endsAt: 'Ends at',
    sendPayLink: 'Send pay link',
    payLinkCreated: 'Pay link created',
    payLinkError: 'Unable to create pay link',
    noSubscription: 'No subscription found for this customer.',
    selectSubscriptionCta: 'Select a subscription',
    usageOverview: 'Usage overview',
    usagePeriod: 'Current period usage and limits. Period: {{start}} → {{end}} ({{tz}})',
    noUsage: 'No usage recorded for this period.',
    used: 'used',
    limit: 'Limit',
    unlimited: 'Unlimited',
    unlimitedUsage: 'Unlimited usage',
    usageRecords: 'Usage records',
    usageRecordsDescription: 'Recent usage events for this period.',
    allMetrics: 'All metrics',
    metricFilter: 'Filter metric',
    recordedAt: 'Recorded at',
    metric: 'Metric',
    endpoint: 'Endpoint',
    credits: 'Credits',
    cost: 'Cost',
    pageXofY: 'Page {{page}}{{of}}',
    of: 'of',
    prev: 'Prev',
    next: 'Next',
    noUsageFiltered: 'No usage records for the selected filters.',
    noUsageRecords: 'No usage records available.',
    recentInvoices: 'Recent invoices',
    invoicesDescription: 'Latest invoices synced from billing.',
    invoice: 'Invoice',
    issued: 'Issued',
    noInvoices: 'No invoices available.',
    paymentMethod: 'Payment method',
    paymentMethodDescription: 'Default payment method on file.',
    card: 'Card',
    default: 'Default',
    expires: 'Expires',
    funding: 'Funding',
    noPaymentMethod: 'No payment method on file.',
    paymentUnavailable: 'Payment method unavailable.',
    upcomingPhases: 'Upcoming phases',
    nextInvoice: 'Next invoice',
    nextInvoiceValue: '{{amount}} • {{date}}',
    priceUpdate: 'Update price',
    priceUpdateValue: 'From {{from}} to {{to}}{{interval}}',
    metrics: {
      apiCalls: 'API calls',
      storage: 'Storage',
      seats: 'Seats',
      creditDeduction: 'Credits deducted',
    },
    usageStatus: {
      within: 'Within',
      nearing: 'Nearing limit',
      exceeded: 'Exceeded',
      unlimited: 'Unlimited',
    },
  } as const;

const customers = {
    title: 'Customer Management',
    subtitle: 'Manage individual customers and their team relationships',
    create: {
      title: 'Create customer',
      description: 'Invite an individual or team owner to your provider portal.',
      success: 'Customer created successfully',
      error: 'Failed to create customer',
      submit: 'Create customer',
      fields: {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email address',
        emailDescription: 'We will send account activation instructions to this address.',
        accountType: 'Account type',
        accountTypeDescription: 'Choose whether you are adding an individual or creating a workspace owner.',
        individual: 'Individual',
        teamOwner: 'Team owner',
        teamName: 'Workspace name',
        teamNamePlaceholder: 'e.g., Acme Platform Team',
        company: 'Company',
        phone: 'Phone number',
        initialProduct: 'Add a starting product',
        selectProduct: 'Select a product',
        noProduct: 'Do not assign a product',
        initialProductDescription: 'Optional: automatically provision access to one of your products.',
      },
    },
    teams: {
      title: 'Team workspace',
      subtitleWithProvider: 'Manage invitations, roles, and seat usage for {{providerName}} automation workspace.',
      subtitleWithoutProvider: 'Manage invitations, roles, and seat usage for your automation workspace.',
      create: {
        title: 'Create a team workspace',
        description: 'Set up a shared workspace and invite teammates once your team is ready.',
        fields: {
          name: 'Team name',
          description: 'Description',
          slug: 'Workspace slug',
          maxSeats: 'Seat limit',
        },
        placeholders: {
          name: 'e.g., Acme Platform Team',
          description: 'What does this team manage?',
          slug: 'acme-platform',
          maxSeats: '10',
        },
        helper: 'Your personal subscription will be converted to a team workspace plan after creation.',
        submit: 'Create team',
        submitting: 'Creating team…',
        success: 'Team created. Syncing your workspace.',
        errors: {
          generic: 'Unable to create a team right now.',
        },
        validation: {
          nameRequired: 'Team name is required.',
          descriptionMax: 'Description is too long.',
          slugMax: 'Slug is too long.',
          seatsInvalid: 'Seat limit must be a number.',
          seatsRange: 'Seat limit must be between 1 and 1000.',
        },
        ineligibleTitle: 'Team creation unavailable',
        ineligibleDescription: 'Only individual customers can create a new team workspace.',
        ineligibleHelp: 'If you need a new team, ask your current workspace owner to update your role.',
        subscriptionCanceled: 'Your personal subscription will end at the end of the current billing period.',
        subscriptionCanceledWithDate: 'Your personal subscription will end on {{date}}.',
      },
      activeTeamLabel: 'Active team',
      selectPlaceholder: 'Choose a team',
      seatUsageLabel: 'Seat usage',
      seatUsageHelper: '{{percentage}}% of available seats in use',
      stats: {
        activeMembers: {
          label: 'Active members',
          helper: 'Includes owners, admins, and members',
        },
        pendingInvitations: {
          label: 'Pending invitations',
          helperPending: 'Follow up with pending invitees',
          helperEmpty: 'No pending invitations',
        },
        monthlySpend: {
          label: 'Monthly spend',
          helper: 'Team share of product subscriptions',
        },
      },
      roles: {
        owner: 'Owner',
        admin: 'Admin',
        member: 'Member',
      },
      members: {
        title: 'Members',
        description: 'Adjust roles and remove access for your workspace collaborators.',
        tableHeaders: {
          name: 'Name',
          role: 'Role',
          status: 'Status',
          credits: 'Credits',
          lastActive: 'Last active',
          actions: 'Actions',
        },
        credits: {
          usage: '{{used}} / {{limit}} credits',
          usageUnlimited: '{{used}} credits used',
          remaining: '{{remaining}} credits left',
          limitReached: 'Limit reached',
        },
        actionsMenuAria: 'Open member actions',
        actionsLabel: 'Actions',
        limitUsage: 'Limit usage',
        remove: 'Remove',
        removeTitle: 'Remove {{memberName}} from {{teamName}}?',
        removeDescription:
          'This member will immediately lose access to all team resources, and their team-scoped API keys will be revoked right away. You can re-invite them later if needed.',
        removeConfirm: 'Remove member',
      },
      invite: {
        title: 'Invite teammates',
        description:
          'Send a workspace invitation with the right permissions. New members count toward your seat limit.',
        fields: {
          email: 'Email address',
          role: 'Role',
          message: 'Message (optional)',
        },
        placeholder: {
          email: 'alex@example.com',
          message: 'Add context for the invitee',
        },
        seatLimitReached:
          'Your team has reached the current seat limit. Remove a member or upgrade seats to invite more people.',
        submit: 'Send invitation',
        submitting: 'Sending invitation…',
      },
      pending: {
        title: 'Pending invitations',
        description: 'Keep track of outstanding invites and nudge teammates as needed.',
        empty: 'No pending invitations right now.',
        invitedBy: 'Invited by {{invitedBy}} on {{invitedAt}}{{expires}}',
        expires: ' • Expires {{date}}',
        managementComingSoon: 'Invitation management coming soon',
        actions: {
          resend: 'Resend',
          revoke: 'Revoke',
        },
        status: {
          pending: 'Pending',
          revoked: 'Revoked',
          accepted: 'Accepted',
          declined: 'Declined',
          expired: 'Expired',
        },
      },
      snapshot: {
        title: 'Workspace snapshot',
        description: 'High-level overview of your team footprint.',
        totalCustomers: 'Total customers',
        teamsCreated: 'Teams created',
        teamOwners: 'Team owners',
        teamMembers: 'Team members',
        individualsHelper: '{{formattedCount}} individual-only',
      },
      dialog: {
        title: 'Credit usage limit',
        titleWithMember: 'Credit usage limit for {{name}}',
        description:
          'Cap how many workspace credits this teammate can use before we route requests through an approver.',
        enforceLabel: 'Enforce credit ceiling',
        enforceDescription: 'Turn off to restore unlimited credit usage for this teammate.',
        creditLabel: 'Monthly credit allowance',
        creditDescription:
          'Blocks additional usage once the member consumes this many credits within the calendar month.',
        emailAlertsLabel: 'Email alerts',
        emailAlertsDescription:
          'Let the member and workspace owners know when the credit ceiling is hit. We also notify them once usage reaches 80% of the allowance.',
        cancel: 'Cancel',
        save: 'Save limit',
        saving: 'Saving…',
      },
      validation: {
        invalidEmail: 'Enter a valid email address',
        messageTooLong: 'Keep the invitation message under 280 characters',
        creditRequired: 'Enter a credit amount',
        creditNonNegative: 'Provide a non-negative credit amount',
      },
      fallback: {
        loadErrorTitle: 'Teams',
        loadErrorDescription: "We couldn't load your team data right now.",
        loadErrorHelp: 'Please refresh the page or contact support@florentin.app if the issue persists.',
        emptyTitle: 'Create your first team',
        emptyDescription: 'Invite teammates once you have set up a collaborative workspace.',
        emptyHelp:
          'Ask your provider admin to provision a team for this account. Once available, you can manage invitations and roles here.',
      },
    },
    totalRequests: 'Total Requests',
    responseTime: 'Response Time',
    successRate: 'Success Rate',
    issues: '{{count}} Issues',
    fixIssues: 'Fix Issues',
    
    // Customer List
    addCustomer: 'Add Customer',
    totalCustomers: 'Total Customers',
    teamOwners: 'Team Owners',
    teamMembers: 'Team Members',
    individualOnly: 'Individual Only',
    totalTeams: 'Total Teams',
    avgProductsPerCustomer: 'Avg Products/Customer',
    searchPlaceholder: 'Search by name, email, company, or team...',
    allRoles: 'All Roles',
    owners: 'Team Owners',
    members: 'Team Members',
    individual: 'Individual Only',
    mixedRoles: 'Mixed Roles',
    allStatus: 'All Status',
    active: 'Active',
    trial: 'Trial',
    inactive: 'Inactive',
    suspended: 'Suspended',
    moreFilters: 'More Filters',
    
    // Table Headers
    customer: 'Customer',
    rolesAndTeams: 'Roles & Teams',
    subscriptions: 'Subscriptions',
    status: 'Status',
    usage: 'Usage',
    monthlyValue: 'Monthly Value',
    created: 'Created',
    operations: 'operations',
    
    // Role Labels
    owner: 'Owner',
    member: 'Member',
    
    // Actions
    viewDetails: 'View Details',
    editCustomer: 'Edit Customer',
    manageSubscriptions: 'Manage Subscriptions',
    suspendAccount: 'Suspend Account',
    
    // Expanded Details
    subscriptionDetails: 'Subscription Details',
    individualSubscriptions: 'Individual Subscriptions',
    teamOwnerships: 'Team Ownerships',
    teamMemberships: 'Team Memberships',
    team: 'Team',
    plan: 'Plan',
    
    // Customer Detail
    rolesSummary: '{{ownerCount}} Team Owner | {{memberCount}} Team Member | Individual Subscriber',
    lastActive: 'Last Active',
    accountMetrics: 'Account Metrics',
    totalMonthlySpend: 'Total Monthly Spend',
    accountHealthScore: 'Account Health Score',
    productsUsed: 'Products Used',
    
    // Tabs
    overview: 'Overview',
    profile: 'Profile',
    apiKeys: 'API Keys',
    billing: 'Billing',
    teamMembersTab: 'Team Members',
    
    // Sections
    personalInformation: 'Personal Information',
    contactDetails: 'Contact Details',
    communicationPreferences: 'Communication Preferences',
    tagsAndAttributes: 'Tags & Custom Attributes',
    accountSettings: 'Account Settings',
    ownedSubscriptions: 'Owned Subscriptions',
    
    // Team Management
    seatsUsed: '{{used}} of {{total}} seats used',
    manageTeam: 'Manage Team',
    addMembers: 'Add Members',
    viewTeam: 'View Team',
    leaveTeam: 'Leave Team',
    teamOwner: 'Team Owner',
    yourUsage: 'Your Usage',
    nextBilling: 'Next billing',
    
    // API Keys
    personalApiKeys: 'Personal API Keys',
    teamApiKeys: 'Team API Keys',
    createPersonalKey: 'Create Personal Key',
    viewTeamKeys: 'View Team Keys',
    manageTeamKeys: 'Manage Team Keys',
    
    // Create Customer Dialog (expanded copy)
    createDialog: {
      title: 'Create New Customer',
      description: 'Add a new customer to your platform',
      submit: 'Create Customer',
      fields: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email Address',
        emailDescription: 'Will be used for login and notifications',
        company: 'Company (Optional)',
        phone: 'Phone (Optional)',
        timezone: 'Timezone',
        timezoneSelect: 'Select a timezone',
        accountType: 'Account Type',
        accountTypeDescription: 'Choose whether this is an individual account or team owner',
        individual: 'Individual Account',
        teamOwner: 'Team Owner',
        teamName: 'Team Name',
        teamNamePlaceholder: 'Enter team name',
        initialProduct: 'Initial Product (Optional)',
        selectProduct: 'Select a product',
        noProduct: 'No initial product',
        initialProductDescription: 'Optionally subscribe the customer to a product immediately'
      }
    },

    register: {
      title: 'Create Customer Account',
      subtitle: 'Set up a new customer and optionally provision their team',
      individualCopy: 'Creates a personal account with individual subscriptions.',
      teamOwnerCopy: 'Assigns ownership of a shared team workspace with seat management.',
      teamSectionTitle: 'Team Ownership Details',
      teamSectionDescription: 'Provide the team name for the base plan.',
      totalSeatsLabel: 'Included Seats',
      totalSeatsDescription: 'How many seats are covered before additional seat pricing applies.',
      sendInviteLabel: 'Send welcome email invitation',
      sendInviteDescription: 'Email the customer with next steps and a link to activate their account.',
      termsLabelPrefix: 'I agree to the',
      termsLabelSuffix: '',
      termsRequired: 'You must agree to the Terms of Service to continue.',
      validation: {
        firstNameRequired: 'Please provide a first name.',
        lastNameRequired: 'Please provide a last name.',
        emailInvalid: 'Please provide a valid email address.',
        teamNameRequired: 'Team name is required for team owners.',
        workspaceNameRequired: 'Please provide a workspace name for the team.',
      },
      submitting: 'Creating account...',
      submitCta: 'Create Customer Account',
      haveAccount: 'Already manage customers?',
      successTitle: 'Customer Ready to Go',
      successSubtitle: 'We created the account, synced the latest team setup, and sent a verification email. Ask the customer to confirm their address before they sign in.',
      successToast: 'Customer registered successfully.',
      errorToast: 'Unable to register the customer. Please try again.',
      teamErrorToast: 'Team provisioning failed. You can configure the team later.',
      teamSummary: '{{members}} of {{total}} seats currently allocated.',
      createAnotherCta: 'Register Another Customer',
      summary: {
        customerName: 'Customer Name',
        customerEmail: 'Email Address',
        company: 'Company',
        teamName: 'Team',
        seats: '{{used}} used of {{total}} seats',
      },
      loginRequiredTitle: 'Sign in to manage customers',
      loginRequiredDescription: 'Log in with your provider account before creating new customers.',
    },
    
    // Edit Customer Dialog
    edit: {
      title: 'Edit Customer',
      description: 'Update customer information',
      submit: 'Save Changes',
      ownerWarning: 'This customer is a team owner. Changes will affect all team members.',
      memberWarning: 'This customer is a team member. Some settings are managed by the team owner.',
      fields: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email Address',
        emailNote: 'Email cannot be changed after account creation',
        company: 'Company',
        phone: 'Phone',
        timezone: 'Timezone',
        status: 'Account Status',
        statusDescription: 'Control customer access to the platform',
        twoFactor: 'Two-Factor Authentication',
        twoFactorDescription: 'Require additional verification for login'
      }
    },
    detail: {
      resetPassword: 'Reset Password',
      suspendAccount: 'Suspend Account',
      growthIndicator: '+12% from last month',
      creditBalance: 'Credit Balance',
      creditsLabel: 'credits',
      creditsUsed: '{{count}} credits used this month',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      customerId: 'Customer ID',
      language: 'Language',
      timezone: 'Timezone',
      ownedSubscriptionsDescription: 'Products and teams this customer owns or subscribes to individually',
      individualSubscriptions: 'Individual Subscriptions',
      teamSubscriptionsOwner: 'Team Subscriptions (Owner)',
      allSubscriptions: 'All Subscriptions',
      apiKeys: 'API Keys',
      teamApiKeys: 'Team API Keys',
      billingHistory: 'Billing History',
      allSubscriptionsDescription: 'Manage all subscriptions associated with this customer',
      monthlyTeamSpendDescription: 'Total cost for all team subscriptions',
      monthlyTeamSpendTitle: 'Monthly Team Spend',
      membersCount: '{{count}} members',
      teamMemberContext: 'As a team member, you have access to team resources but billing is managed by the team owner.',
      personalKeysDescription: 'API keys owned by this customer for individual use',
      teamKeysDescription: 'API keys from teams this customer belongs to',
      billingHistoryDescription: 'Recent invoices and payments',
      columns: {
        name: 'Name',
        key: 'Key',
        team: 'Team',
        created: 'Created',
        date: 'Date',
        product: 'Product',
        type: 'Type',
        plan: 'Plan',
        status: 'Status',
        creditUsage: 'Credit Usage',
        monthlyCost: 'Monthly Cost',
        nextBilling: 'Next Billing',
        seats: 'Seats',
        monthlySpend: 'Monthly Spend',
        lastActive: 'Last Active',
        actions: 'Actions',
        issued: 'Issued',
        lastUsed: 'Last Used',
        permissions: 'Permissions',
        amount: 'Amount',
        description: 'Description'
      },
      actions: {
        upgradePlan: 'Upgrade Plan',
        viewUsage: 'View Usage',
        cancelSubscription: 'Cancel',
        download: 'Download',
        refresh: 'Refresh',
        rotate: 'Rotate',
        regenerate: 'Regenerate',
        revoke: 'Revoke',
        viewTeamKeys: 'View keys'
      },
      badges: {
        individual: 'Individual',
        team: 'Team',
        owner: 'Owner',
        member: 'Member'
      },
      show: 'Show',
      hide: 'Hide',
      copyKey: 'Copy API Key',
      masked: 'Hidden',
      apiKeyLabel: 'API Key',
      noTeamKeys: 'No team API keys available.',
      refreshKeys: 'Refresh Keys',
      rotateKey: 'Rotate Key',
      sampleProducts: {
        emailApi: 'Email API',
        smsApi: 'SMS API',
        analyticsApi: 'Analytics API',
        workflowApi: 'Workflow API'
      },
      planLabel: '{{plan}} Plan',
      monthlyValue: '${{amount}}/mo',
      operationsCount: '{{formattedCount}} operations',
      operationsCount_one: '{{formattedCount}} operation',
      operationsCount_other: '{{formattedCount}} operations',
      teamPlanSummary: '{{plan}} Plan • {{seats}} seats',
      creditUsageSummary: '{{used}} / {{limit}} credits',
      teamAccess: 'Team Access',
      teamManaged: 'Team managed',
      totalSpentTitle: 'Total Spent',
      totalSpentSubtitle: 'Lifetime value',
      creditBalanceCardTitle: 'Credit Balance',
      creditBalanceSubtitle: 'Available credits',
      nextPaymentTitle: 'Next Payment',
      nextPaymentSubtitle: 'Due on {{date}}',
      nextBillingLabel: 'Next billing: {{date}}',
      profileTitle: 'Profile overview',
      profileSubtitle: 'Core attributes synced from your automation platform.',
      loadingName: 'Customer',
      none: 'Not provided',
      noTeams: 'Individual account',
      noData: 'Customer details unavailable.',
      nextStepsTitle: 'Automation tips',
      nextStepsDescription:
        'Use service tokens to schedule syncs and deliver a seamless customer portal experience.',
      tipTokens: 'Rotate portal tokens regularly',
      tipTokensDescription:
        'Remove unused service tokens in the Provider API area to prevent stale automation credentials.',
      tipSso: 'Audit SSO activity',
      tipSsoDescription:
        'Track issued links and disable any integration issuing unexpected portal sessions.',
      tipTeams: 'Team access detected',
      tipTeamsDescription:
        'Team owners inherit the same portal exposure as individual logins. Confirm downstream tools enforce seat limits.',
      openPortal: 'Open customer portal',
      ssoConfirmTitle: 'Generate secure portal link?',
      ssoConfirmDescription:
        'A single-use magic link will be issued for {{name}}. They will be signed into the portal immediately.',
      ssoConfirmAction: 'Issue link',
      ssoIssuing: 'Generating link…',
      ssoLinkIssued: 'Customer portal opened in a new tab.',
      ssoLinkError: 'Unable to issue portal link. Try again later.',
      loadError: 'Failed to load customer.',
      retry: 'Retry'
    },
    checkoutLinks: {
      title: 'Checkout links',
      description: 'Generate tokenized checkout links for {{name}} and review recent activity.',
      generateTitle: 'Generate a new checkout link',
      generateDescription: 'Choose product, plan/package, and pricing from billing setup.',
      generateAction: 'Generate link',
      generatePending: 'Generating...',
      generateSuccess: 'Checkout link generated.',
      generateError: 'Unable to generate checkout link.',
      latestTitle: 'Latest checkout link',
      expiresAtLabel: 'Expires {{date}}',
      copyAction: 'Copy link',
      copyDone: 'Copied',
      copySuccess: 'Checkout link copied.',
      copyError: 'Unable to copy checkout link.',
      openAction: 'Open link',
      urlUnavailable: 'Checkout URL unavailable.',
      listTitle: 'Generated links',
      listDescription: 'Previous links and status for this customer.',
      listNote: 'URLs are not returned for previous links. Generate a new link to share.',
      refresh: 'Refresh',
      loading: 'Loading checkout links...',
      retry: 'Retry',
      empty: 'No checkout links generated yet.',
      optionsError: 'Checkout link options are unavailable.',
      loadError: 'Unable to load checkout links.',
      table: {
        status: 'Status',
        product: 'Product',
        planPackage: 'Plan / Package',
        pricing: 'Pricing',
        currency: 'Currency',
        created: 'Created',
        expires: 'Expires',
        used: 'Used'
      },
      form: {
        productId: 'Product',
        productIdPlaceholder: 'Select a product',
        noProducts: 'No products available',
        selectionType: 'Checkout type',
        selectionTypePlaceholder: 'Select plan or package',
        selectionPlan: 'Subscription plan',
        selectionPackage: 'Credit package',
        planId: 'Plan',
        planIdPlaceholder: 'Select a plan',
        noPlans: 'No plans available',
        variantLabel: 'Variant',
        packageId: 'Package',
        packageIdPlaceholder: 'Select a package',
        noPackages: 'No packages available',
        priceId: 'Price',
        priceIdPlaceholder: 'Select a price',
        noPrices: 'No prices available',
        expiresAt: 'Expires at',
        expiresAtPlaceholder: 'dd/mm/yyyy',
        expiresAtHint: 'Defaults to 1 day from now.',
        planHint: 'Use credit packages for one-time credit purchases.',
        packageHint: 'Packages are used for credit bundles.',
        priceSummary: 'Currency {{currency}} - {{cycle}}',
        priceSummaryNoCycle: 'Currency {{currency}}'
      },
      validation: {
        productRequired: 'Product ID is required.',
        cycleRequired: 'Billing cycle ID is required.',
        priceRequired: 'Price ID is required.',
        planRequired: 'Plan selection is required.',
        packageRequired: 'Package selection is required.',
        priceInvalid: 'Selected price is invalid.',
        expiresAtInvalid: 'Expiration date is invalid.',
        expiresAtFuture: 'Expiration must be in the future.'
      },
      status: {
        active: 'Active',
        expired: 'Expired',
        consumed: 'Consumed'
      },
      planLabel: 'Plan',
      packageLabel: 'Package',
      cycleLabel: 'Cycle',
      priceLabel: 'Price'
    },
    providerList: {
      heading: 'Manage individual customers and their team relationships',
      retry: 'Try again',
      empty: 'No customers match your filters yet.',
      refresh: 'Refresh',
      addCustomer: 'Add Customer',
      trial: 'Trial',
      trialEnds: 'Ends {{date}}',
      trialDays: 'Trial: {{count}} days',
      trialRemaining_one: '{{count}} day left',
      trialRemaining_other: '{{count}} days left',
      teamsAndRoles: 'Teams & Roles',
      joined: 'Joined {{date}}',
      created: 'Created {{date}}',
      updated: 'Last updated {{date}}',
      stats: {
        totalCustomers: 'Total Customers',
        teamOwners: 'Team Owners',
        teamMembers: 'Team Members',
        individuals: 'Individuals',
        trialing: 'On Trial',
        uniqueTeams: 'Unique Teams'
      },
      statusFallback: {
        all: 'All Status',
        active: 'Active',
        inactive: 'Inactive',
        trial: 'Trial',
        suspended: 'Suspended',
        pendingVerification: 'Pending Verification'
      },
      filters: {
        allRoles: 'All Roles',
        owner: 'Team Owners',
        admin: 'Team Admins',
        member: 'Team Members',
        individual: 'Individuals',
        allTeams: 'All Teams',
        searchPlaceholder: 'Search by name, email, or company…',
        teamPlaceholder: 'All teams',
      },
      views: {
        active: 'Active',
        deleted: 'Archived',
      },
      deleted: {
        empty: 'No archived customers found.',
      },
      actions: {
        viewCustomer: 'View details',
        editCustomer: 'Edit Customer',
        manageSubscriptions: 'Manage Subscriptions',
        checkoutLinks: 'Checkout links',
        suspendAccount: 'Suspend Account',
        deleteCustomer: 'Archive customer',
        restoreCustomer: 'Restore customer',
      },
      errors: {
        forbidden: 'You are not allowed to perform this action.',
        unauthorized: 'Your session expired. Please sign in again.',
        notFound: 'Customer was not found for this provider.',
      },
      delete: {
        title: 'Delete customer',
        titleSoft: 'Archive customer',
        titleHard: 'Delete customer permanently',
        description: 'Choose how to remove this customer.',
        soft: 'Archive',
        hard: 'Delete permanently',
        hardWarning: 'Permanent deletion is irreversible. Type the customer email to confirm.',
        activeSubscriptionTitle: 'Permanent deletion is blocked due to an active subscription.',
        activeSubscriptionBlocked: 'Cancel or terminate the active subscription before permanent deletion.',
        subscriptionPlan: 'Plan',
        subscriptionStatus: 'Status',
        subscriptionPeriodEnd: 'Current period end (UTC)',
        switchToArchive: 'Switch to archive',
        reason: 'Reason (optional)',
        deleting: 'Deleting...',
        confirmSoft: 'Archive customer',
        confirmHard: 'Delete permanently',
        success: 'Customer deleted successfully.',
        error: 'Unable to delete customer.',
        confirmMismatch: 'Please type the exact customer email to confirm permanent deletion.',
      },
      restore: {
        title: 'Restore customer',
        description: 'Restore {{email}} to active customers?',
        restoring: 'Restoring...',
        success: 'Customer restored successfully.',
        error: 'Unable to restore customer.',
      },
      table: {
        customer: 'Customer',
        status: 'Status',
        trial: 'Trial',
        teams: 'Teams & Roles',
        created: 'Created',
        actions: 'Actions'
      },
      metrics: {
        teamsCount: 'Teams: {{count}}'
      },
      pagination: {
        range: 'Showing {{from}}-{{to}} of {{total}}',
        perPageLabel: 'Rows per page',
        perPageOption: '{{count}} per page',
        previous: 'Previous',
        next: 'Next',
        pageLabel: 'Page {{current}} of {{total}}'
      }
    },
  } as const;

export default customers;
